import React, { useEffect, useRef, useState } from 'react'
import {
  useController,
  Control,
  FieldValues,
  Path
} from 'react-hook-form'
import useAsyncService from '../../hooks/useAsyncService'
import { getOrganizations } from '../../services/endpoints/organizations'
import './ICSFleetPicker.css'
import ICSErrorCard from '../ICSErrorCard/ICSErrorCard'

interface Fleet {
  id: number
  name: string
}

interface Organization {
  id: number
  name: string
  fleets: Fleet[]
}

export type FleetPickerValue = {
  organizations: number[]
  fleets: number[]
}

export const ensureFleetPickerValue = (
  value: unknown
): FleetPickerValue => {
  if (
    value !== null
    && typeof value === 'object'
    && Array.isArray((value as FleetPickerValue).organizations)
    && Array.isArray((value as FleetPickerValue).fleets)
  ) {
    return value as FleetPickerValue
  }

  return { organizations: [], fleets: [] }
}

export type FleetPickerProps<T extends FieldValues, K extends Path<T>> = {
  name: K
  control: Control<T>
  required?: boolean
  autoSelectAll?: boolean
  onAutoSelected?: (value: FleetPickerValue) => void
}

const loadOrganizationsWithFleets = async (): Promise<Organization[]> => {
  const organizations = await getOrganizations()

  return organizations
    .filter((organization) => !organization.deleteFlag)
    .map((organization) => ({
      id: organization.id,
      name: organization.name,
      fleets: organization.fleets
        .filter((fleet) => !fleet.deleteFlag)
        .map((fleet) => ({
          id: fleet.id,
          name: fleet.name
        }))
        .sort((left, right) => left.name.localeCompare(right.name))
    }))
    .sort((left, right) => left.name.localeCompare(right.name))
}

const ICSFleetPicker = <T extends FieldValues, K extends Path<T>>({
  name,
  control,
  required = true,
  autoSelectAll = false,
  onAutoSelected
}: FleetPickerProps<T, K>) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandList, setExpandList] = useState(false)
  const shouldAutoSelectAll = useRef(autoSelectAll)
  const didAutoSelect = useRef(false)
  const onAutoSelectedRef = useRef(onAutoSelected)
  onAutoSelectedRef.current = onAutoSelected
  const { serviceError, serviceLoading, serviceValue: organizations } = useAsyncService<
    void,
    Organization[]
  >({
    serviceCall: loadOrganizationsWithFleets,
    delay: 0,
    initialArgs: undefined
  })

  const {
    field: { value, onChange }
  } = useController({
    name,
    control,
    rules: required
      ? {
          validate: {
            required: (nextValue) => {
              const normalizedValue = ensureFleetPickerValue(nextValue)
              if (
                normalizedValue.organizations.length < 1
                && normalizedValue.fleets.length < 1
              ) {
                return 'Required'
              }
            }
          }
        }
      : undefined
  })

  useEffect(() => {
    if (
      !shouldAutoSelectAll.current
      || didAutoSelect.current
      || !organizations
      || organizations.length === 0
    ) {
      return
    }

    didAutoSelect.current = true
    const nextValue = {
      organizations: organizations.map((organization) => organization.id),
      fleets: []
    }
    onChange(nextValue)
    onAutoSelectedRef.current?.(nextValue)
  }, [organizations, onChange])

  if(serviceError) {
    return <ICSErrorCard code="error" message={serviceError} title="Error" fields={[]}/>
  }

  const {
    organizations: selectedOrganizations,
    fleets: selectedFleets
  } = ensureFleetPickerValue(value)

  const normalizedSearchTerm = searchTerm.toLowerCase()
  const filteredOrganizations =
    organizations === null
      ? []
      : organizations
          .map((organization) => {
            const matchingFleets = organization.fleets.filter((fleet) =>
              fleet.name.toLowerCase().includes(normalizedSearchTerm)
            )
            const isOrganizationMatch = organization.name
              .toLowerCase()
              .includes(normalizedSearchTerm)

            if (isOrganizationMatch || matchingFleets.length > 0) {
              return {
                ...organization,
                fleets: isOrganizationMatch ? organization.fleets : matchingFleets
              }
            }

            return null
          })
          .filter((organization): organization is Organization => organization !== null)

  const handleOrganizationChange = (organizationId: number) => {
    const isSelected = selectedOrganizations.includes(organizationId)

    if (isSelected) {
      onChange({
        organizations: selectedOrganizations.filter((id) => id !== organizationId),
        fleets: selectedFleets
      })
      return
    }

    onChange({
      organizations: selectedOrganizations.concat([organizationId]),
      fleets: selectedFleets
    })
  }

  const handleFleetChange = (organizationId: number, fleetId: number) => {
    const organization = organizations?.find((candidate) => candidate.id === organizationId)
    const organizationFleetIds = organization?.fleets.map((fleet) => fleet.id) ?? []

    if (!selectedOrganizations.includes(organizationId)) {
      const nextSelectedFleets = selectedFleets.includes(fleetId)
        ? selectedFleets.filter((id) => id !== fleetId)
        : selectedFleets.concat([fleetId])
      const allOrganizationFleetsSelected = organizationFleetIds.length > 0
        && organizationFleetIds.every((id) => nextSelectedFleets.includes(id))

      onChange({
        organizations: allOrganizationFleetsSelected
          ? Array.from(new Set(selectedOrganizations.concat([organizationId])))
          : selectedOrganizations.filter((id) => id !== organizationId),
        fleets: allOrganizationFleetsSelected
          ? nextSelectedFleets.filter((id) => !organizationFleetIds.includes(id))
          : nextSelectedFleets
      })
      return
    }

    onChange({
      organizations: selectedOrganizations.filter((id) => id !== organizationId),
      fleets: selectedFleets
        .filter((id) => !organizationFleetIds.includes(id))
        .concat(organizationFleetIds.filter((id) => id !== fleetId))
    })
  }

  const clearSelections = () => {
    onChange({ organizations: [], fleets: [] })
  }

  const selectAll = () => {
    if (!organizations) {
      return
    }

    onChange({
      organizations: organizations.map((organization) => organization.id),
      fleets: []
    })
  }

  const selectedFleetCount = (() => {
    const selectedFleetIds = new Set(selectedFleets)

    organizations?.forEach((organization) => {
      if (selectedOrganizations.includes(organization.id)) {
        organization.fleets.forEach((fleet) => selectedFleetIds.add(fleet.id))
      }
    })

    return selectedFleetIds.size
  })()

  return (
    <>
      <div className="fleet-picker-header">
        <h3>Organizations/Fleets</h3>
        <div
          className="fleet-picker-expand"
          aria-label="expandlist"
          tabIndex={0}
          role="button"
          onClick={(event) => {
            event.preventDefault()
            setExpandList((currentValue) => !currentValue)
          }}
        >
          <i
            className={
              expandList ? 'dx-icon dx-icon-minus' : 'dx-icon dx-icon-plus'
            }
          ></i>
          <span className="fleet-picker-expand-tooltip">
            Expand or Retract Organizations/Fleets List
          </span>
        </div>
      </div>
      <div className="ics-fleet-picker">
        <div className="fleet-picker-status">
          <span className="fleet-picker-selected-count">
            {selectedFleetCount} fleets selected
          </span>
          <button
            className="fleet-picker-action"
            type="button"
            onClick={(event) => {
              event.preventDefault()
              clearSelections()
            }}
          >
            Clear
          </button>
          <button
            className="fleet-picker-action"
            type="button"
            onClick={(event) => {
              event.preventDefault()
              selectAll()
            }}
          >
            Select All
          </button>
          <div className="fleet-picker-search">
            <input
              type="text"
              placeholder="Search Organizations/Fleets"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <button
              className="fleet-picker-clear-search"
              type="button"
              aria-label="Clear search"
              title="Clear search"
              onClick={(event) => {
                event.preventDefault()
                setSearchTerm('')
              }}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>

        <div
          className={
            expandList ? 'fleet-picker-list fleet-picker-list-expanded' : 'fleet-picker-list'
          }
        >
          {serviceLoading || organizations === null ? (
            <div className="fleet-picker-loading">Loading organizations...</div>
          ) : organizations.length === 0 ? (
            <div className="fleet-picker-empty">There are no organizations available.</div>
          ) : (
            filteredOrganizations.map((organization) => (
              <div key={organization.id} className="fleet-picker-group">
                <label className="fleet-picker-group-label">
                  <input
                    type="checkbox"
                    checked={selectedOrganizations.includes(organization.id)}
                    onChange={() => handleOrganizationChange(organization.id)}
                  />
                  {organization.name}
                </label>
                <div className="fleet-picker-items">
                  {organization.fleets.map((fleet) => (
                    <div key={fleet.id} className="fleet-picker-item">
                      <label className="fleet-picker-item-label">
                        <input
                          type="checkbox"
                          checked={
                            selectedFleets.includes(fleet.id)
                            || selectedOrganizations.includes(organization.id)
                          }
                          onChange={() => {
                            handleFleetChange(organization.id, fleet.id)
                          }}
                        />
                        {fleet.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}

export default ICSFleetPicker